// Spreadsheet Template and Workbook Validation APIs
import { getApiClient, withOrgConfig } from '../quote-api-helpers'
import type {
  TemplateCategory, TemplateStatus,
  TemplateListResponse, TemplateCreateResponse,
  SpreadsheetTemplateDetail,
  WorkbookValidationResponse,
  WorkbookStructureValidationResponse,
  WorkbookBindingsValidationResponse,
} from '../quote-api-spreadsheet-types'

export const spreadsheetTemplateApi = {
  // =========================================================================
  // SPREADSHEET TEMPLATE APIs
  // =========================================================================

  /**
   * List all spreadsheet templates.
   * @param category - Optional filter by category
   * @param status - Optional filter by status (default: 'active')
   */
  async listTemplates(
    category?: TemplateCategory,
    status?: TemplateStatus
  ): Promise<TemplateListResponse> {
    const client = getApiClient()
    const params: Record<string, string> = {}
    if (category) params.category = category
    if (status) params.status = status

    const res = await client.get<TemplateListResponse>(
      `/report/templates/`,
      { ...withOrgConfig(), params }
    )
    return res.data
  },

  /**
   * Get template details including bindings.
   * @param templateId - Template UUID
   */
  async getTemplate(templateId: string): Promise<SpreadsheetTemplateDetail> {
    const client = getApiClient()
    const res = await client.get<SpreadsheetTemplateDetail>(
      `/report/templates/${templateId}/`,
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Create a new template.
   * @param name - Template name
   * @param category - Template category
   * @param description - Optional description
   * @param workbookRevisionId - Optional workbook revision to link
   */
  async createTemplate(
    name: string,
    category: TemplateCategory = 'quote',
    description?: string,
    workbookRevisionId?: string
  ): Promise<TemplateCreateResponse> {
    const client = getApiClient()
    const res = await client.post<TemplateCreateResponse>(
      `/report/templates/`,
      {
        name,
        category,
        description: description || '',
        workbook_revision_id: workbookRevisionId,
      },
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Create template from existing workbook revision.
   * @param name - Template name
   * @param workbookRevisionId - Source workbook revision
   * @param category - Template category
   * @param description - Optional description
   */
  async createTemplateFromWorkbook(
    name: string,
    workbookRevisionId: string,
    category: TemplateCategory = 'quote',
    description?: string
  ): Promise<TemplateCreateResponse> {
    const client = getApiClient()
    const res = await client.post<TemplateCreateResponse>(
      `/report/templates/from-workbook/`,
      {
        name,
        workbook_revision_id: workbookRevisionId,
        category,
        description: description || '',
      },
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Update a template.
   * @param templateId - Template UUID
   * @param updates - Fields to update
   */
  async updateTemplate(
    templateId: string,
    updates: { name?: string; description?: string; category?: TemplateCategory }
  ): Promise<{ message: string; template_id: string; updated_fields: string[] }> {
    const client = getApiClient()
    const res = await client.patch(
      `/report/templates/${templateId}/`,
      updates,
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Delete a template.
   * @param templateId - Template UUID
   */
  async deleteTemplate(templateId: string): Promise<{ message: string }> {
    const client = getApiClient()
    const res = await client.delete(
      `/report/templates/${templateId}/`,
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Activate a template (make available for use).
   * @param templateId - Template UUID
   */
  async activateTemplate(templateId: string): Promise<{ message: string; template_id: string; status: string }> {
    const client = getApiClient()
    const res = await client.post(
      `/report/templates/${templateId}/activate/`,
      {},
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Archive a template (remove from active use).
   * @param templateId - Template UUID
   */
  async archiveTemplate(templateId: string): Promise<{ message: string; template_id: string; status: string }> {
    const client = getApiClient()
    const res = await client.post(
      `/report/templates/${templateId}/archive/`,
      {},
      withOrgConfig()
    )
    return res.data
  },

  /**
   * Upload/update workbook for a template.
   * @param templateId - Template UUID
   * @param file - Excel file to upload
   */
  async uploadTemplateWorkbook(
    templateId: string,
    file: File
  ): Promise<{
    message: string
    template_id: string
    version: number
    workbook_revision_id: string
    template_hash: string
    binding_count: number
  }> {
    const client = getApiClient()
    const formData = new FormData()
    formData.append('file', file)

    const res = await client.post(
      `/report/templates/${templateId}/upload-workbook/`,
      formData,
      {
        ...withOrgConfig(),
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    return res.data
  },

  // =========================================================================
  // WORKBOOK VALIDATION APIs
  // =========================================================================

  /**
   * Validate a workbook against a template's expected bindings.
   * @param templateId - Template UUID
   * @param file - Excel file to validate
   */
  async validateWorkbookAgainstTemplate(
    templateId: string,
    file: File
  ): Promise<WorkbookValidationResponse> {
    const client = getApiClient()
    const formData = new FormData()
    formData.append('file', file)

    const res = await client.post<WorkbookValidationResponse>(
      `/report/templates/${templateId}/validate/`,
      formData,
      {
        ...withOrgConfig(),
        headers: {
          ...withOrgConfig().headers,
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return res.data
  },

  /**
   * Validate basic workbook structure (not against a template).
   * @param file - Excel file to validate
   */
  async validateWorkbookStructure(file: File): Promise<WorkbookStructureValidationResponse> {
    const client = getApiClient()
    const formData = new FormData()
    formData.append('file', file)

    const res = await client.post<WorkbookStructureValidationResponse>(
      `/report/workbook/validate/`,
      formData,
      {
        ...withOrgConfig(),
        headers: {
          ...withOrgConfig().headers,
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return res.data
  },

  /**
   * Validate that a workbook has specific required named ranges.
   * @param file - Excel file to validate
   * @param requiredBindings - List of required binding names
   */
  async validateWorkbookBindings(
    file: File,
    requiredBindings: string[]
  ): Promise<WorkbookBindingsValidationResponse> {
    const client = getApiClient()
    const formData = new FormData()
    formData.append('file', file)
    formData.append('required_bindings', JSON.stringify(requiredBindings))

    const res = await client.post<WorkbookBindingsValidationResponse>(
      `/report/workbook/validate-bindings/`,
      formData,
      {
        ...withOrgConfig(),
        headers: {
          ...withOrgConfig().headers,
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return res.data
  },
}
